import { useState } from 'react'
import { ISelectOption } from 'components/forms/Selects/SelectOption'
import { ILocationSuggestion } from 'transfix-ui/components/SuggestLocation'
import { ISelectProps } from 'transfix-ui'
import { TQuoteFilters } from '../../../types'

interface IUseLocationProps {
  quotesFilters: TQuoteFilters
  setQuotesFilters: React.Dispatch<React.SetStateAction<TQuoteFilters>>
}

export const useLocation = ({ quotesFilters, setQuotesFilters }: IUseLocationProps) => {
  const initialPickupLocation =
    quotesFilters.pickupCity || quotesFilters.pickupStateCode
      ? {
          city: quotesFilters.pickupCity,
          state: quotesFilters.pickupStateCode,
        }
      : null
  const initialDeliveryLocation =
    quotesFilters.deliveryCity || quotesFilters.deliveryStateCode
      ? {
          city: quotesFilters.deliveryCity,
          state: quotesFilters.deliveryStateCode,
        }
      : null
  const [pickupLocation, setPickupLocation] = useState<ILocationSuggestion | null>(
    initialPickupLocation
  )
  const [deliveryLocation, setDeliveryLocation] = useState<ILocationSuggestion | null>(
    initialDeliveryLocation
  )

  const [pickupRange, setPickupRange] = useState<number>(quotesFilters.pickupRange || 0)
  const [deliveryRange, setDeliveryRange] = useState<number>(quotesFilters.deliveryRange || 0)

  const rangeOptions: ISelectOption[] = [
    { label: '0 mi', value: 0 },
    { label: '+5 mi', value: 5 },
    { label: '+10 mi', value: 10 },
    { label: '+20 mi', value: 20 },
    { label: '+50 mi', value: 50 },
    { label: '+100 mi', value: 100 },
  ]

  const selectPickupLocationHandler = (value: ILocationSuggestion) => {
    setPickupLocation(value)
    if (value.state) {
      setQuotesFilters({
        ...quotesFilters,
        pickupCity: value?.city,
        pickupStateCode: value?.state,
      })
    } else {
      const updatedQuotes = { ...quotesFilters }
      delete updatedQuotes.pickupCity
      delete updatedQuotes.pickupStateCode
      setQuotesFilters({ ...updatedQuotes })
    }
  }

  const selectDeliveryLocationHandler = (value: ILocationSuggestion) => {
    setDeliveryLocation(value)
    if (value.state) {
      setQuotesFilters({
        ...quotesFilters,
        deliveryCity: value?.city,
        deliveryStateCode: value?.state,
      })
    } else {
      const updatedQuotes = { ...quotesFilters }
      delete updatedQuotes.deliveryCity
      delete updatedQuotes.deliveryStateCode
      setQuotesFilters({ ...updatedQuotes })
    }
  }

  const selectPickupRangeHandler: ISelectProps['onChange'] = event => {
    if (event.target.value === undefined) {
      const updatedQuotes = { ...quotesFilters }
      delete updatedQuotes.pickupRange
      setQuotesFilters({ ...updatedQuotes })
    } else if (typeof event.target.value === 'number') {
      setPickupRange(event.target.value)
      setQuotesFilters({
        ...quotesFilters,
        pickupRange: event.target.value,
      })
    }
  }

  const selectDeliveryRangeHandler: ISelectProps['onChange'] = event => {
    if (event.target.value === undefined) {
      const updatedQuotes = { ...quotesFilters }
      delete updatedQuotes.deliveryRange
      setQuotesFilters({ ...updatedQuotes })
    } else if (typeof event.target.value === 'number') {
      setDeliveryRange(event.target.value)
      setQuotesFilters({
        ...quotesFilters,
        deliveryRange: event.target.value,
      })
    }
  }

  const clearAllLocationFiltersHandler = () => {
    setPickupRange(0)
    setDeliveryRange(0)
    setDeliveryLocation(null)
    setPickupLocation(null)
  }

  return {
    selectPickupLocationHandler,
    selectDeliveryLocationHandler,
    selectPickupRangeHandler,
    selectDeliveryRangeHandler,
    clearAllLocationFiltersHandler,
    pickupLocation,
    deliveryLocation,
    pickupRange,
    deliveryRange,
    rangeOptions,
  }
}
